<!-- ダッシュボード セレクション選択時のページ -->
<script setup lang="ts">
  import { ref } from "vue";
  import { useFilterStore } from "~/stores/FilterStore";
  import { useLibraryStore } from "~/stores/LibraryStore";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { Library, emptyLibrary } from "~/entities/Library";

  import LiveViewForContent from "~/components/Organisms/LiveViewForContent.vue";
  import MovieViewForContent from "~/components/Organisms/MovieViewForContent.vue";
  import DocumentViewForContent from "~/components/Organisms/DocumentViewForContent.vue";
  import ArticleView from "~/components/Organisms/ArticleView.vue";
  import { useHistoryStore } from "~/stores/HistoryStore";
  import { CONTENT_MENU_ID } from "~/consts/contentsConfig";
  import { getFirstMenu } from "~/utils/Plan";

  const historyStore = useHistoryStore();

  const libraryStore = useLibraryStore();
  const filterStore = useFilterStore();

  interface Props {
    planInfo: PlanInfo;
  }
  const props = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });
  const query = useRoute().query;
  const isTablet = isTabletAndSpDisplayRef();
  const showDetail = ref(query.showDetail === "true");

  onMounted(() => {
    filterStore.resetWithPlanId(props.planInfo.id);
    fetch();
  });

  const liveView = ref<InstanceType<typeof LiveViewForContent> | null>(null);
  const movieView = ref<InstanceType<typeof MovieViewForContent> | null>(null);
  const documentView = ref<InstanceType<typeof DocumentViewForContent> | null>(null);
  const articleView = ref<InstanceType<typeof ArticleView> | null>(null);

  // 高さ調整
  const contentRef = ref<HTMLDivElement | undefined>(undefined);
  const contentWrapperHeight = computed<string | null>(() => {
    const rect = contentRef.value?.getBoundingClientRect();
    if (rect) return `calc(100svh - ${rect.y}px)`;
    return null;
  });

  const selectedId = ref<string>(String(useRoute().query.content || ""));
  const selectedLibrary = computed<Library>({
    get: () => {
      return libraryStore.selectedLibrary;
    },
    set: value => {
      libraryStore.selectedLibrary = value;
    },
  });

  const getLibraries = async () => {
    await libraryStore.getLibraries(props.planInfo.id);
    selectedLibrary.value =
      libraryStore.libraries.find(library => String(library.libraryId) === String(selectedId.value)) || emptyLibrary();
  };

  // 初期化したかどうか
  const initLibraries = ref(false);

  const changeItem = async (id: string) => {
    // v-ifで消さないとinifinite scrollが動かないので、一度emptyにして、nextTickでawaitしてから取得する
    selectedLibrary.value = emptyLibrary();
    await nextTick();

    selectedLibrary.value =
      libraryStore.libraries.find(library => String(library.libraryId) === String(id)) || emptyLibrary();

    if (id === CONTENT_MENU_ID.LIVE) {
      liveView.value?.fetch();
    } else if (id === CONTENT_MENU_ID.MOVIE) {
      movieView.value?.fetch();
    } else if (id === CONTENT_MENU_ID.DOCUMENT) {
      documentView.value?.fetch();
    } else if (id === CONTENT_MENU_ID.ARTICLE) {
      articleView.value?.fetch();
    }

    selectedId.value = id;
    showDetail.value = true;
  };

  const initSelectedId = () => {
    if (!selectedId.value || selectedId.value === "") {
      // クエリがない場合はprops.planInfo.menuSortの最初の項目を選択
      selectedId.value = getFirstMenu(props.planInfo);
    }
  };

  const fetch = async () => {
    if (initLibraries.value) {
      return;
    }
    initSelectedId();
    initLibraries.value = true;
    await getLibraries();
    if (selectedId.value === CONTENT_MENU_ID.LIVE) {
      liveView.value?.fetch();
    } else if (selectedId.value === CONTENT_MENU_ID.MOVIE) {
      movieView.value?.fetch();
    } else if (selectedId.value === CONTENT_MENU_ID.DOCUMENT) {
      documentView.value?.fetch();
    } else if (selectedId.value === CONTENT_MENU_ID.ARTICLE) {
      articleView.value?.fetch();
    }
  };

  const handleOnBack = () => {
    showDetail.value = false;
    historyStore.back();
  };

  watch(
    () => useRoute().query.showDetail,
    val => {
      showDetail.value = val === "true";
    },
  );

  watch(
    () => useRoute().query.content,
    val => {
      selectedId.value = String(val) || getFirstMenu(props.planInfo);
    },
  );

  defineExpose({
    fetch,
  });
</script>

<template>
  <div v-if="!initLibraries"></div>
  <div v-else ref="contentRef" class="content_wrapper">
    <OrganismsContentMenuList
      :class="{ 'd-none': isTablet && showDetail }"
      :plan-info="props.planInfo"
      :libraries="libraryStore.libraries"
      :selected-id="!isTablet ? selectedId : ''"
      @on-click-item="changeItem"
    />

    <div
      v-show="selectedId === CONTENT_MENU_ID.LIVE"
      class="content_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsLiveViewForContent ref="liveView" :plan-info="props.planInfo" @on-back="handleOnBack" />
    </div>

    <div
      v-show="selectedId === CONTENT_MENU_ID.MOVIE"
      class="content_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsMovieViewForContent ref="movieView" :plan-info="props.planInfo" @on-back="handleOnBack" />
    </div>

    <div
      v-show="selectedId === CONTENT_MENU_ID.DOCUMENT"
      class="content_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsDocumentViewForContent ref="documentView" :plan-info="props.planInfo" @on-back="handleOnBack" />
    </div>
    <div
      v-show="selectedId === CONTENT_MENU_ID.ARTICLE"
      class="content_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsArticleView ref="articleView" :plan-info="props.planInfo" @on-back="handleOnBack" />
    </div>

    <div
      v-if="
        ![CONTENT_MENU_ID.LIVE, CONTENT_MENU_ID.MOVIE, CONTENT_MENU_ID.DOCUMENT, CONTENT_MENU_ID.ARTICLE].includes(
          selectedId,
        ) && selectedLibrary.libraryId != ''
      "
      class="content_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsLibraryView :selected-library="selectedLibrary" :plan-info="props.planInfo" @on-back="handleOnBack" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .content_wrapper {
    display: table;
    max-width: 1280px;
    width: 100%;
    height: v-bind("contentWrapperHeight");
    position: relative;
    @include display_sp {
      width: 100%;
      display: inline-table;
    }
  }

  .content_detail {
    display: table-cell;
    width: calc(100% - 248px);
    height: 100%;
    vertical-align: top;
    position: relative;
  }

  @include display_tablet {
    .content_wrapper {
      width: 100vw;
      height: initial !important;
      .content_detail {
        position: fixed;
        width: 100vw;
        height: 100svh;
        z-index: 1000;
        top: 0;
        left: 0;
        background: black;
        overflow-y: scroll;
      }
    }
  }
</style>
